import { useEffect, useMemo, useRef, useState } from "react";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { findOwningAgentId } from "../agent/ownership.js";
import { useThailandJobEmployersList, THAILAND_JOBS_EMPLOYERS_DEFAULT_PAGE_SIZE } from "./useThailandJobEmployersList.js";
import { getEmployer, createEmployer, updateEmployer, getEmployerOptions, getJob } from "./thailandJobsApi.js";
import { listAgents } from "../workerLine/workerLineApi.js";
import EmployerFormView, { TAX_ID_DETAIL_NOTE } from "./EmployerFormView.jsx";
import EmployerJobsDialog from "./EmployerJobsDialog.jsx";
import JobBuilderDialog from "./JobBuilderDialog.jsx";
import "./thailandJobsEmployers.css";
import "../pages/states.css";
import Pagination from "../components/Pagination.jsx";

import PageSkeleton, { DetailSkeleton } from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "Thailand Jobs", "Employers"];
const DRAWER_TITLE_ID = "tj-drawer-title";
const SEARCH_DEBOUNCE_MS = 350;
const VIEW_ONLY_ROLES = ["Plc", "SuperAgent"];
// The Agent directory the create form's owner picker is filled from. 100 is the
// server's own maximum page size for that list; the picker asks for one page
// and says so when the line is longer than that, rather than paging silently.
const AGENT_DIRECTORY_PAGE_SIZE = 100;

const HERO_COPY_TEXT =
  "ฝั่งงานในไทย — จัดการข้อมูลนายจ้าง และตำแหน่งงานที่รอ Matching ในขั้นตอนถัดไป Agent เป็นผู้ key งานเข้าระบบโดยตรง หน้านี้ไม่ต้องใช้ Invite Card ในการสร้างข้อมูลนายจ้าง";
const FOOTNOTE_TEXT =
  "Agent เป็นผู้ key นายจ้างและงานเข้าระบบโดยตรงในฝั่งงานในไทยนี้ — หน้านี้ไม่ต้องใช้ Invite Card ในการสร้างข้อมูลนายจ้าง (Invite Card สำหรับนายจ้างเป็นลิงก์แยกต่างหากที่ Agent ส่งให้นายจ้างกรอกข้อมูลเอง ไม่ใช่ขั้นตอนบนหน้านี้) การสร้างงานที่นี่เป็นเพียงการบันทึกความต้องการแรงงานเพื่อรอ Matching ในขั้นตอนถัดไป ยังไม่ใช่การจับคู่แรงงาน ไม่สร้าง assignment และไม่เริ่มการติดตามอุปกรณ์";
const CONTACT_RESERVED_TEXT = "ดูในรายละเอียด";
const PLC_SUPER_AGENT_VIEW_ONLY_TEXT =
  "PLC / Super Agent: มุมมองอ่านอย่างเดียว (view-only) สำหรับหน้านี้ — เห็นรายการและรายละเอียดได้ครบ แต่ปุ่มสร้างนายจ้าง/งานจะแสดงเป็น disabled";

const STATUS_BADGE_VARIANT = { Active: "success", Suspended: "danger", Inactive: "neutral" };

const EMPTY_CREATE_EMPLOYER_FORM = {
  agentId: "",
  legalName: "",
  displayName: "",
  taxId: "",
  businessType: "",
  primaryContactName: "",
  primaryPhoneNumber: "",
  lineContact: "",
  addressText: "",
  // P160: the detailed employer record. The form used to capture nine fields
  // and call that an employer; an operator matching workers to a job needs to
  // know what kind of company this is, how big it is, and where it actually
  // sits - none of which the sketch recorded.
  entityType: "",
  taxBranchCode: "",
  establishedOn: "",
  registeredCapital: "",
  employeeCountRange: "",
  companyBackground: "",
  addressLine: "",
  subDistrict: "",
  district: "",
  province: "",
  postalCode: "",
  primaryContactPosition: "",
  emailContact: "",
  websiteContact: "",
};

/**
 * P160: the structured address parts, joined into the legacy free-text
 * AddressText column.
 *
 * AddressText is deliberately KEPT rather than replaced: several screens and
 * the employer detail response already read it, and dropping it would blank
 * them. Composing it here means the one address an operator types shows up
 * correctly in both the new structured fields and every existing reader,
 * without asking them to type it twice. If they typed nothing structured, the
 * free-text box they filled in instead is used as-is.
 */
function composeAddressText(form) {
  const parts = [
    form.addressLine,
    form.subDistrict && `ต.${form.subDistrict}`,
    form.district && `อ.${form.district}`,
    form.province,
    form.postalCode,
  ]
    .map((part) => (part ?? "").trim())
    .filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : form.addressText.trim() || null;
}

function statusBadgeVariant(status) {
  return STATUS_BADGE_VARIANT[status] ?? "neutral";
}

function describeListError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์เข้าถึงรายชื่อนายจ้างนี้";
  }
  return "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ";
}

function describeDetailError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูรายละเอียดนายจ้างนี้";
  }
  if (error?.status === 404) {
    return "ไม่พบนายจ้างนี้ อาจถูกลบหรือย้ายแล้ว";
  }
  return "ไม่สามารถโหลดรายละเอียดได้ กรุณาลองใหม่อีกครั้ง";
}

function describeCreateEmployerError(error) {
  if (error?.validation) {
    return "กรุณากรอก Agent เจ้าของ, ชื่อนิติบุคคล, ชื่อผู้ติดต่อหลัก และเบอร์โทรผู้ติดต่อหลักให้ครบถ้วน";
  }
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์สร้างนายจ้างสำหรับสายงานนี้ (SuperAdmin หรือ Agent เจ้าของสายงานเท่านั้น)";
  }
  if (error?.status === 404) {
    return "ไม่พบ Agent ที่เลือก กรุณาเลือกใหม่อีกครั้ง";
  }
  if (error?.status === 400) {
    return "ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบฟอร์มอีกครั้ง";
  }
  return "ไม่สามารถสร้างนายจ้างได้ กรุณาลองใหม่อีกครั้ง";
}

function describeUpdateEmployerError(error) {
  if (error?.validation) {
    return "กรุณากรอกชื่อนิติบุคคลและชื่อผู้ติดต่อหลักให้ครบถ้วน";
  }
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์แก้ไขนายจ้างรายนี้ (SuperAdmin หรือ Agent เจ้าของสายงานเท่านั้น)";
  }
  if (error?.status === 404) {
    return "ไม่พบนายจ้างนี้ อาจถูกลบหรือย้ายแล้ว";
  }
  if (error?.status === 400) {
    return "ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบฟอร์มอีกครั้ง";
  }
  return "ไม่สามารถบันทึกการแก้ไขได้ กรุณาลองใหม่อีกครั้ง";
}

/**
 * The stored employer record as form values.
 *
 * Two fields are deliberately NOT prefilled from the detail response:
 * `taxId`, because the response only ever carries the masked value and
 * writing dots back would destroy the real number, and the phone/Line
 * channels, because the update contract has no field for them at all.
 */
function buildEditEmployerForm(detail) {
  return {
    ...EMPTY_CREATE_EMPLOYER_FORM,
    employerId: detail.employerId,
    legalName: detail.legalName ?? "",
    displayName: detail.displayName ?? "",
    entityType: detail.entityType ?? "",
    businessType: detail.businessType ?? "",
    taxBranchCode: detail.taxBranchCode ?? "",
    // A DateTime from the API; the date input wants YYYY-MM-DD. Sliced, never
    // re-parsed through the browser's zone, which would shift the day.
    establishedOn: detail.establishedOn ? String(detail.establishedOn).slice(0, 10) : "",
    registeredCapital: detail.registeredCapital == null ? "" : String(detail.registeredCapital),
    employeeCountRange: detail.employeeCountRange ?? "",
    companyBackground: detail.companyBackground ?? "",
    addressLine: detail.addressLine ?? "",
    subDistrict: detail.subDistrict ?? "",
    district: detail.district ?? "",
    province: detail.province ?? "",
    postalCode: detail.postalCode ?? "",
    addressText: detail.addressText ?? "",
    primaryContactName: detail.primaryContactName ?? "",
    primaryContactPosition: detail.primaryContactPosition ?? "",
    employerStatus: detail.employerStatus ?? "Active",
  };
}

/** Two-letter monogram from a company name - never a fabricated logo. */
function monogram(name) {
  const trimmed = (name ?? "").trim();
  if (!trimmed) {
    return "-";
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function formatDateTimeTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart} ICT`;
}

function uniqueAgents(items) {
  const seen = new Map();
  for (const item of items) {
    const agentId = item.agent?.agentId;
    if (agentId && !seen.has(agentId)) {
      seen.set(agentId, item.agent);
    }
  }
  return [...seen.values()];
}

function uniqueSuperAgents(items) {
  const seen = new Map();
  for (const item of items) {
    const id = item.line?.superAgentOrganizationId;
    if (id && !seen.has(id)) {
      seen.set(id, { id, name: item.line?.superAgentName });
    }
  }
  return [...seen.values()];
}

function uniquePlcs(items) {
  const seen = new Map();
  for (const item of items) {
    const id = item.line?.plcOrganizationId;
    if (id && !seen.has(id)) {
      seen.set(id, { id, name: item.line?.plcName });
    }
  }
  return [...seen.values()];
}

function uniqueEmployerStatuses(items) {
  const seen = new Map();
  for (const item of items) {
    if (item.employerStatus && !seen.has(item.employerStatus)) {
      seen.set(item.employerStatus, item.employerStatus);
    }
  }
  return [...seen.values()];
}

/**
 * /backoffice/thailand-jobs/employers - the Thailand Jobs Employers screen
 * (Prompt 102B, built from the W48 reference). Read-heavy list/detail plus
 * two create actions over the Prompt 101 API: employers and jobs.
 * This is job-supply-side data intake only - it never calls a matching/
 * assignment/tracking/Invite-Card API (none of those exist for this side of
 * the platform), and no form on this page ever collects or sends a workerId.
 *
 * Since 2026-08-05 this is also the ONLY sidebar entry into the job side
 * ("job ให้รวมเข้าที่ employer เลยครับ ไม่ต้องแยกอีกเมนูครับ" - owner): a job is
 * reached from the row's own งาน button, and the standalone Jobs screen stays
 * registered in App.jsx for the two things it still owns (Edit Job, Mark Ready
 * For Matching). Worksites are gone from the product entirely (2026-08-08,
 * owner: "ไซด์งานในแอฟนี้ไม่ได้ใช้") - no screen, no picker, no column.
 *
 * Filtering/pagination is real server-side (mirrors
 * useReadyForMatchCandidatesList.js / useWorkerProfilesList.js), and only
 * ever forwards the P101-whitelisted query params (see thailandJobsApi.js).
 * Agent/Super Agent/PLC/Status filter options are derived client-side from
 * the currently loaded page's own real item fields (this endpoint's summary
 * DTO has no per-dimension breakdown) - a real, disclosed page-scoped
 * limitation, never a fabricated option list.
 */
export default function ThailandJobsEmployersPage() {
  const { data: identity } = useBackofficeIdentity();
  const primaryRole = identity?.primaryRole;
  const ownAgentId = useMemo(() => findOwningAgentId(identity?.accessScopes), [identity]);
  const isViewOnly = VIEW_ONLY_ROLES.includes(primaryRole);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [employerStatusFilter, setEmployerStatusFilter] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [superAgentFilter, setSuperAgentFilter] = useState("");
  const [plcFilter, setPlcFilter] = useState("");
  const [page, setPage] = useState(1);

  const [selectedEmployerId, setSelectedEmployerId] = useState(null);
  const [drawerView, setDrawerView] = useState(null); // null | "detail" | "create-employer"
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null, forId: null });
  const [detailReloadToken, setDetailReloadToken] = useState(0);

  const [createEmployerForm, setCreateEmployerForm] = useState(EMPTY_CREATE_EMPLOYER_FORM);
  // P160: vocabularies for the create form's dropdowns. Null until loaded; the
  // form renders its selects disabled with an honest note rather than an empty
  // list that looks like "no options exist".
  const [employerOptions, setEmployerOptions] = useState(null);
  const [createEmployerSubmit, setCreateEmployerSubmit] = useState({ status: "idle", error: null });
  // The Agents an employer may be created under. Read from the Agent directory,
  // NOT from the employers on screen: an Agent who owns no employer yet is
  // exactly the one a new employer is most likely being keyed in for, and
  // deriving the picker from the loaded rows left it empty on an empty list.
  const [agentDirectory, setAgentDirectory] = useState({ status: "idle", items: [], truncated: false });
  const agentDirectoryStartedRef = useRef(false);
  // The same form, opened on an existing record: clicking a row lands here.
  const [editEmployerForm, setEditEmployerForm] = useState({ ...EMPTY_CREATE_EMPLOYER_FORM, employerId: null, employerStatus: "Active" });
  const [editEmployerSubmit, setEditEmployerSubmit] = useState({ status: "idle", error: null });

  // The งาน dialog - one employer's own positions, opened by the งาน button on
  // that employer's row/card. Holds the clicked list item (not just the id) so
  // the dialog can title itself immediately, without waiting for the detail
  // fetch the same click starts.
  const [jobsDialogEmployer, setJobsDialogEmployer] = useState(null);
  const [jobBuilderOpen, setJobBuilderOpen] = useState(false);
  // P161: the position the builder is editing, or null when creating a new one.
  // Held as the full detail rather than the list row, because the list row
  // carries no description, skills or matching requirements - opening the
  // builder on it would silently blank every field it does not include.
  const [jobBeingEdited, setJobBeingEdited] = useState(null);
  const [jobsReloadToken, setJobsReloadToken] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, employerStatusFilter, agentFilter, superAgentFilter, plcFilter]);

  const filters = useMemo(
    () => ({
      page,
      pageSize: THAILAND_JOBS_EMPLOYERS_DEFAULT_PAGE_SIZE,
      search,
      employerStatus: employerStatusFilter,
      agentId: agentFilter,
      superAgentOrganizationId: superAgentFilter,
      plcOrganizationId: plcFilter,
    }),
    [page, search, employerStatusFilter, agentFilter, superAgentFilter, plcFilter]
  );

  const { status: listStatus, data: listData, error: listError, refetch: refetchList } = useThailandJobEmployersList(filters);

  // Detail is fetched whenever selectedEmployerId changes (row/card opened,
  // or a create-employer success moves selection to the new employer) - it
  // employer, and only refetches for the same id when Refresh is pressed or
  // a job is created (detailReloadToken).
  useEffect(() => {
    if (!selectedEmployerId) {
      setDetailState({ status: "idle", data: null, error: null, forId: null });
      return undefined;
    }

    let cancelled = false;
    setDetailState((previous) => ({
      status: "loading",
      data: previous.forId === selectedEmployerId ? previous.data : null,
      error: null,
      forId: selectedEmployerId,
    }));

    getEmployer(selectedEmployerId)
      .then((data) => {
        if (!cancelled) {
          setDetailState({ status: "ready", data, error: null, forId: selectedEmployerId });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailState({ status: "error", data: null, error, forId: selectedEmployerId });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- detailReloadToken intentionally re-triggers a fetch for the same selectedEmployerId
  }, [selectedEmployerId, detailReloadToken]);

  // Prefill the edit form the moment this employer's record arrives, and only
  // for a record the form is not already holding - a refetch after a save (or
  // after a job is created) must not overwrite whatever
  // the operator has typed since.
  useEffect(() => {
    if (detailState.status !== "ready" || !detailState.data) {
      return;
    }
    const detail = detailState.data;
    setEditEmployerForm((previous) => (previous.employerId === detail.employerId ? previous : buildEditEmployerForm(detail)));
  }, [detailState]);

  function openEmployerDetail(employerId) {
    setJobsDialogEmployer(null);
    setEditEmployerSubmit({ status: "idle", error: null });
    setSelectedEmployerId(employerId);
    setDrawerView("detail");
  }

  /**
   * The row's own action. Selecting the employer also starts the detail fetch:
   * the job builder needs this employer's owning Agent, and
   * nextActions.canCreateJob is the only honest source for whether this actor
   * may add a position at all.
   */
  function openEmployerJobs(item) {
    setDrawerView(null);
    setSelectedEmployerId(item.employerId);
    setJobsDialogEmployer(item);
    setJobBuilderOpen(false);
    setJobBeingEdited(null);
  }

  function closeJobsDialog() {
    setJobsDialogEmployer(null);
    setJobBuilderOpen(false);
    setJobBeingEdited(null);
    setSelectedEmployerId(null);
  }

  function handleJobCreated() {
    // The list behind the builder counts this employer's jobs, and the dialog
    // the builder opened over is showing the very list it belongs in.
    setJobBuilderOpen(false);
    setJobBeingEdited(null);
    setJobsReloadToken((token) => token + 1);
    refetchList();
    setDetailReloadToken((token) => token + 1);
  }

  /**
   * P161: opens the builder on one existing position.
   *
   * Fetches the FULL job detail first: the row in the list carries only what the
   * list needs, so opening the builder on it would prefill the form with blanks
   * for description, skills and every matching requirement - and saving would
   * then write those blanks back over real stored values.
   */
  function openEditJob(item) {
    getJob(item.jobId)
      .then((detail) => {
        setJobBeingEdited(detail);
        setJobBuilderOpen(true);
      })
      .catch(() => {
        // The builder never opens on a job that could not be read - a form
        // full of blanks that saves over the record is worse than no form.
        setJobBeingEdited(null);
      });
  }

  function openCreateEmployerDrawer() {
    setCreateEmployerForm(EMPTY_CREATE_EMPLOYER_FORM);
    // A directory read that failed is retried the next time the form is opened
    // - the operator's own "close it and try again" has to actually try again.
    if (agentDirectory.status === "error") {
      agentDirectoryStartedRef.current = false;
      setAgentDirectory({ status: "idle", items: [], truncated: false });
    }
    setCreateEmployerSubmit({ status: "idle", error: null });
    setDrawerView("create-employer");
  }

  function closeDrawer() {
    setDrawerView(null);
    setSelectedEmployerId(null);
  }

  function handleRefresh() {
    refetchList();
    if (selectedEmployerId) {
      setDetailReloadToken((token) => token + 1);
    }
  }

  function updateCreateEmployerField(field, value) {
    setCreateEmployerForm((previous) => ({ ...previous, [field]: value }));
  }

  function updateEditEmployerField(field, value) {
    setEditEmployerForm((previous) => ({ ...previous, [field]: value }));
  }

  useEffect(() => {
    // Both the create form and the edit form fill their dropdowns from these.
    if ((drawerView !== "create-employer" && drawerView !== "detail") || employerOptions) {
      return;
    }
    let cancelled = false;
    getEmployerOptions()
      .then((data) => {
        if (!cancelled) {
          setEmployerOptions(data);
        }
      })
      // A failed vocabulary fetch must not block the whole form: the fields
      // that are free text still work, and the selects say why they cannot.
      .catch(() => {
        if (!cancelled) {
          setEmployerOptions({ entityTypes: [], businessTypes: [], employeeCountRanges: [], provinces: [] });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [drawerView, employerOptions]);

  useEffect(() => {
    // The owner picker on the create form. Only SuperAdmin ever sees a picker
    // (an Agent creates under themselves), but the Agent-role field shows that
    // Agent's own code/name from this same list, so both roles need it.
    // Guarded by a ref, not by the status this effect writes: keying the effect
    // on its own state would re-run it the moment it set "loading", and that
    // re-run's cleanup would cancel the read still in flight.
    if (drawerView !== "create-employer" || agentDirectoryStartedRef.current) {
      return;
    }
    agentDirectoryStartedRef.current = true;
    let cancelled = false;
    setAgentDirectory({ status: "loading", items: [], truncated: false });
    listAgents({ status: "Active", page: 1, pageSize: AGENT_DIRECTORY_PAGE_SIZE })
      .then((data) => {
        if (cancelled) {
          return;
        }
        const items = (data?.items ?? []).map((agent) => ({
          agentId: agent.agentId,
          agentCode: agent.agentCode,
          displayName: agent.displayName,
          // Which line this Agent belongs to. Two Agents can carry near-identical
          // names on the worker side and the job side, and picking the wrong one
          // files the employer under a line that will never work the job.
          plcName: agent.plcName ?? null,
        }));
        setAgentDirectory({
          status: "ready",
          items,
          truncated: (data?.totalCount ?? items.length) > items.length,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setAgentDirectory({ status: "error", items: [], truncated: false });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [drawerView]);

  async function handleCreateEmployerSubmit(event) {
    event.preventDefault();
    const resolvedAgentId = ownAgentId || createEmployerForm.agentId;
    if (!resolvedAgentId || !createEmployerForm.legalName.trim() || !createEmployerForm.primaryContactName.trim() || !createEmployerForm.primaryPhoneNumber.trim()) {
      setCreateEmployerSubmit({ status: "error", error: { validation: true } });
      return;
    }
    setCreateEmployerSubmit({ status: "submitting", error: null });
    try {
      const created = await createEmployer({
        agentId: resolvedAgentId,
        legalName: createEmployerForm.legalName.trim(),
        displayName: createEmployerForm.displayName.trim() || null,
        taxId: createEmployerForm.taxId.trim() || null,
        businessType: createEmployerForm.businessType.trim() || null,
        primaryContactName: createEmployerForm.primaryContactName.trim(),
        primaryPhoneNumber: createEmployerForm.primaryPhoneNumber.trim(),
        lineContact: createEmployerForm.lineContact.trim() || null,
        addressText: composeAddressText(createEmployerForm),
        entityType: createEmployerForm.entityType || null,
        taxBranchCode: createEmployerForm.taxBranchCode.trim() || null,
        establishedOn: createEmployerForm.establishedOn || null,
        // An empty string would post as 0; a capital nobody entered is null.
        registeredCapital: createEmployerForm.registeredCapital === "" ? null : Number(createEmployerForm.registeredCapital),
        employeeCountRange: createEmployerForm.employeeCountRange || null,
        companyBackground: createEmployerForm.companyBackground.trim() || null,
        addressLine: createEmployerForm.addressLine.trim() || null,
        subDistrict: createEmployerForm.subDistrict.trim() || null,
        district: createEmployerForm.district.trim() || null,
        province: createEmployerForm.province || null,
        postalCode: createEmployerForm.postalCode.trim() || null,
        primaryContactPosition: createEmployerForm.primaryContactPosition.trim() || null,
      });
      setCreateEmployerSubmit({ status: "success", error: null });
      refetchList();
      // Land on the new employer's own detail view - a real end-to-end
      // demonstration of the create flow, and avoids a second fetch.
      setSelectedEmployerId(created.employerId);
      setDrawerView("detail");
    } catch (error) {
      setCreateEmployerSubmit({ status: "error", error });
    }
  }

  /**
   * Saves the record the form was opened on. Every field is sent as the form
   * holds it, except the ones the update contract cannot carry: `taxId` goes
   * only when the operator actually typed a replacement (empty means keep the
   * stored number, never blank it), and the phone/Line channels are not part
   * of this request at all.
   */
  async function handleEditEmployerSubmit(event) {
    event.preventDefault();
    if (!selectedEmployerId || !editEmployerForm.legalName.trim() || !editEmployerForm.primaryContactName.trim()) {
      setEditEmployerSubmit({ status: "error", error: { validation: true } });
      return;
    }
    setEditEmployerSubmit({ status: "submitting", error: null });
    try {
      await updateEmployer(selectedEmployerId, {
        legalName: editEmployerForm.legalName.trim(),
        displayName: editEmployerForm.displayName.trim() || null,
        taxId: editEmployerForm.taxId.trim() || null,
        businessType: editEmployerForm.businessType || null,
        primaryContactName: editEmployerForm.primaryContactName.trim(),
        addressText: composeAddressText(editEmployerForm),
        entityType: editEmployerForm.entityType || null,
        taxBranchCode: editEmployerForm.taxBranchCode.trim() || null,
        establishedOn: editEmployerForm.establishedOn || null,
        registeredCapital: editEmployerForm.registeredCapital === "" ? null : Number(editEmployerForm.registeredCapital),
        employeeCountRange: editEmployerForm.employeeCountRange || null,
        companyBackground: editEmployerForm.companyBackground.trim() || null,
        addressLine: editEmployerForm.addressLine.trim() || null,
        subDistrict: editEmployerForm.subDistrict.trim() || null,
        district: editEmployerForm.district.trim() || null,
        province: editEmployerForm.province || null,
        postalCode: editEmployerForm.postalCode.trim() || null,
        primaryContactPosition: editEmployerForm.primaryContactPosition.trim() || null,
        employerStatus: editEmployerForm.employerStatus || null,
      });
      setEditEmployerSubmit({ status: "success", error: null });
      // The row behind the drawer carries the name and status that just
      // changed, so the list is refetched too - not only the record.
      refetchList();
      setDetailReloadToken((token) => token + 1);
      // A saved tax id must not linger in the field: what is stored is now the
      // masked value shown above it.
      setEditEmployerForm((previous) => ({ ...previous, taxId: "" }));
    } catch (error) {
      setEditEmployerSubmit({ status: "error", error });
    }
  }

  const items = listData?.items ?? [];
  const summary = listData?.summary ?? null;
  const totalCount = listData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / THAILAND_JOBS_EMPLOYERS_DEFAULT_PAGE_SIZE));
  const hasActiveFilters = Boolean(search || employerStatusFilter || agentFilter || superAgentFilter || plcFilter);

  const agentOptions = useMemo(() => uniqueAgents(items), [items]);
  const superAgentOptions = useMemo(() => uniqueSuperAgents(items), [items]);
  const plcOptions = useMemo(() => uniquePlcs(items), [items]);
  const employerStatusOptions = useMemo(() => uniqueEmployerStatuses(items), [items]);

  const isInitialLoading = listStatus === "loading" && listData === null;
  const isInitialError = listStatus === "error" && listData === null;

  // The detail behind the งาน dialog - only once it is this employer's own,
  // never the previously opened employer's record.
  const jobsDialogDetail =
    jobsDialogEmployer && detailState.status === "ready" && detailState.forId === jobsDialogEmployer.employerId ? detailState.data : null;
  const canCreateJobInDialog = jobsDialogDetail?.nextActions?.canCreateJob === true && !isViewOnly;
  const createJobDisabledReason = isViewOnly
    ? PLC_SUPER_AGENT_VIEW_ONLY_TEXT
    : jobsDialogDetail
      ? "คุณไม่มีสิทธิ์สร้างงานให้นายจ้างรายนี้"
      : "กำลังตรวจสอบสิทธิ์สำหรับนายจ้างรายนี้...";

  if (isInitialLoading) {
    return (
      <AppShell crumbs={CRUMBS}>
        <PageSkeleton variant="table" label="กำลังโหลดรายชื่อนายจ้าง..." />
      </AppShell>
    );
  }

  if (isInitialError) {
    return (
      <AppShell crumbs={CRUMBS}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดรายชื่อนายจ้างได้</h1>
          <p className="wx-state-body">{describeListError(listError)}</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-tj-page">
        <div className="wx-tj-toolbar">
          <label className="wx-tj-search">
            <span className="wx-visually-hidden">ค้นหาชื่อนายจ้าง บริษัท ผู้ติดต่อ หรือเลขผู้เสียภาษี</span>
            <input
              type="search"
              placeholder="ค้นหาชื่อนายจ้าง บริษัท ผู้ติดต่อ หรือเลขผู้เสียภาษี"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>

          <div className="wx-tj-filters">
            <label className="wx-tj-filter">
              <span>Agent</span>
              <select value={agentFilter} onChange={(event) => setAgentFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {agentOptions.map((agent) => (
                  <option key={agent.agentId} value={agent.agentId}>
                    {agent.agentCode} · {agent.displayName}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-tj-filter">
              <span>Super Agent</span>
              <select value={superAgentFilter} onChange={(event) => setSuperAgentFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {superAgentOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-tj-filter">
              <span>PLC / บมจ</span>
              <select value={plcFilter} onChange={(event) => setPlcFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {plcOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-tj-filter">
              <span>สถานะนายจ้าง</span>
              <select value={employerStatusFilter} onChange={(event) => setEmployerStatusFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {employerStatusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="wx-tj-toolbar-actions">
            {/* wx-tj-refresh-button/wx-tj-create-employer-button are also
                listed in DetailDrawer's selectableSelector below - without
                them, clicking either while the drawer is open is treated as
                an "outside click" as far as the drawer's own
                close-on-outside-click listener is concerned. */}
            <button type="button" className="wx-button wx-button--ghost wx-tj-refresh-button" onClick={handleRefresh}>
              Refresh
            </button>
            <button
              type="button"
              className="wx-button wx-button--primary wx-tj-create-employer-button"
              disabled={isViewOnly}
              title={isViewOnly ? PLC_SUPER_AGENT_VIEW_ONLY_TEXT : undefined}
              onClick={openCreateEmployerDrawer}
            >
              + สร้างนายจ้าง
            </button>
          </div>
        </div>

        <header className="wx-tj-head">
          <div className="wx-tj-title">
            <h1>
              Employers <span className="wx-tj-title-th">นายจ้าง</span>
            </h1>
            <p>{HERO_COPY_TEXT}</p>
          </div>
          {primaryRole && (
            <span className="wx-tj-scope-pill">
              Scope <b>{primaryRole}</b>
            </span>
          )}
        </header>

        <div className="wx-tj-metrics">
          <MetricCard label="Employers" value={summary?.employerCount} note="นายจ้างทั้งหมดในขอบเขตที่มองเห็น" />
          <MetricCard label="Active" value={summary?.activeEmployerCount} note="สถานะ Active พร้อมใช้งาน" variant="green" />
            <MetricCard label="Open Jobs" value={summary?.openJobCount} note="ตำแหน่งงานสถานะ Open" variant="amber" />
          <MetricCard label="Required Workers" value={summary?.totalDemandCount} note="ผลรวมจำนวนที่ต้องการจากทุกงาน" />
        </div>

        <section className="wx-panel wx-tj-list-panel">
          <div className="wx-tj-list-head">
            <h2 className="wx-panel-title">รายชื่อนายจ้าง</h2>
            <span className="wx-tj-list-count">
              {totalCount} รายการ{listStatus === "loading" && listData !== null ? " · กำลังโหลด..." : ""}
            </span>
          </div>

          {listStatus === "error" && listData !== null && (
            <p className="wx-tj-inline-error" role="alert">
              {describeListError(listError)}
            </p>
          )}

          {items.length === 0 ? (
            <p className="wx-empty-list">{hasActiveFilters ? "ไม่พบรายการที่ตรงกับตัวกรองหรือคำค้นหา" : "ยังไม่มีนายจ้างในขอบเขตที่มองเห็น"}</p>
          ) : (
            <>
              <div className="wx-tj-table-wrap">
                <table className="wx-tj-table">
                  <colgroup>
                    <col style={{ width: "24%" }} />
                    <col style={{ width: "19%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "11%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "11%" }} />
                    <col style={{ width: "9%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Employer</th>
                      <th>Agent / Line</th>
                      <th>Contact</th>
                      <th>Jobs</th>
                      <th>Required Workers</th>
                      <th>Status</th>
                      <th aria-hidden="true">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.employerId} className="wx-tj-row" onClick={() => openEmployerDetail(item.employerId)}>
                        <td>
                          <div className="wx-tj-employer-cell">
                            <span className="wx-tj-avatar" aria-hidden="true">
                              {monogram(item.displayName || item.legalName)}
                            </span>
                            <div>
                              <span className="wx-tj-cell-strong">{item.legalName || "-"}</span>
                              <span className="wx-tj-cell-sub">
                                {item.employerCode} · {item.displayName ?? "-"}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="wx-tj-cell-strong">
                            {item.agent?.agentCode ?? "-"} · {item.agent?.displayName ?? "-"}
                          </span>
                          <span className="wx-tj-cell-sub">
                            {item.line?.superAgentName ?? "-"} · {item.line?.plcName ?? "-"}
                          </span>
                        </td>
                        <td>
                          <span className="wx-tj-cell-sub">{CONTACT_RESERVED_TEXT}</span>
                        </td>
                        <td>
                          <span className="wx-tj-cell-strong">{item.openJobCount}</span>
                          <span className="wx-tj-cell-sub">เปิดรับ</span>
                        </td>
                        <td>
                          <span className="wx-tj-cell-strong">{item.totalDemandCount}</span>
                          <span className="wx-tj-cell-sub">อัตรา</span>
                        </td>
                        <td>
                          <span className={`wx-badge wx-badge--${statusBadgeVariant(item.employerStatus)}`}>{item.employerStatus}</span>
                        </td>
                        <td>
                          {/* The row itself still opens the employer record;
                              this button goes straight to what the operator
                              opens an employer for most often - the positions
                              being recruited for. */}
                          <button
                            type="button"
                            className="wx-tj-row-action"
                            onClick={(event) => {
                              event.stopPropagation();
                              openEmployerJobs(item);
                            }}
                          >
                            งาน
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="wx-tj-cards">
                {items.map((item) => (
                  <EmployerCard
                    key={item.employerId}
                    item={item}
                    onOpen={() => openEmployerDetail(item.employerId)}
                    onOpenJobs={() => openEmployerJobs(item)}
                  />
                ))}
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </section>

        <p className="wx-tj-footnote">{FOOTNOTE_TEXT}</p>
      </div>

      <DetailDrawer
        open={drawerView !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="tr.wx-tj-row, .wx-tj-card, .wx-tj-refresh-button, .wx-tj-create-employer-button"
        panelClassName="wx-drawer-panel--wide"
      >
        {drawerView === "create-employer" && (
          <EmployerFormView
            titleId={DRAWER_TITLE_ID}
            mode="create"
            form={createEmployerForm}
            onChange={updateCreateEmployerField}
            onSubmit={handleCreateEmployerSubmit}
            onCancel={closeDrawer}
            submitState={createEmployerSubmit}
            errorText={describeCreateEmployerError(createEmployerSubmit.error)}
            agentOptions={agentDirectory.items}
            agentOptionsStatus={agentDirectory.status}
            agentOptionsTruncated={agentDirectory.truncated}
            ownAgentId={ownAgentId}
            primaryRole={primaryRole}
            employerOptions={employerOptions}
          />
        )}
        {drawerView === "detail" && (
          <EmployerDetailView
            titleId={DRAWER_TITLE_ID}
            state={detailState}
            primaryRole={primaryRole}
            editForm={editEmployerForm}
            onEditChange={updateEditEmployerField}
            onEditSubmit={handleEditEmployerSubmit}
            onCloseDrawer={closeDrawer}
            // P161: a logo/cover/document write already returns the re-read
            // employer, but the drawer keeps its own copy - so nudge the same
            // reload token every other write here uses rather than teaching the
            // media panel to write into this page's state directly.
            onMediaChanged={() => setDetailReloadToken((token) => token + 1)}
            editSubmitState={editEmployerSubmit}
            editErrorText={describeUpdateEmployerError(editEmployerSubmit.error)}
            employerOptions={employerOptions}
          />
        )}
        {/* A position is not created in this drawer: it is created in the job
            builder the งาน dialog opens. */}
      </DetailDrawer>

      <EmployerJobsDialog
        open={jobsDialogEmployer !== null && !jobBuilderOpen}
        employerId={jobsDialogEmployer?.employerId ?? null}
        employerName={jobsDialogEmployer?.legalName || jobsDialogEmployer?.displayName}
        employerCode={jobsDialogEmployer?.employerCode}
        canCreateJob={canCreateJobInDialog}
        createJobDisabledReason={createJobDisabledReason}
        onCreateJob={() => {
          setJobBeingEdited(null);
          setJobBuilderOpen(true);
        }}
        onEditJob={canCreateJobInDialog ? openEditJob : null}
        reloadToken={jobsReloadToken}
        onClose={closeJobsDialog}
      />

      <JobBuilderDialog
        open={jobBuilderOpen}
        employer={jobsDialogDetail}
        agentId={jobsDialogDetail?.agent?.agentId ?? jobsDialogEmployer?.agent?.agentId ?? null}
        job={jobBeingEdited}
        onClose={() => {
          setJobBuilderOpen(false);
          setJobBeingEdited(null);
        }}
        onCreated={handleJobCreated}
      />
    </AppShell>
  );
}

function MetricCard({ label, value, note, variant, reserved, reservedNote, reservedValue }) {
  if (reserved) {
    return (
      <div className="wx-tj-metric wx-tj-metric--reserved">
        <span className="wx-tj-metric-label">{label}</span>
        <strong className="wx-tj-metric-value wx-tj-metric-value--reserved">{reservedValue ?? "-"}</strong>
        <span className="wx-tj-metric-reserved-pill">{reservedNote}</span>
      </div>
    );
  }
  const variantClass = variant ? ` wx-tj-metric--${variant}` : "";
  return (
    <div className={`wx-tj-metric${variantClass}`}>
      <span className="wx-tj-metric-label">{label}</span>
      <strong className="wx-tj-metric-value">{value ?? "-"}</strong>
      {note && <span className="wx-tj-metric-note">{note}</span>}
    </div>
  );
}

function EmployerCard({ item, onOpen, onOpenJobs }) {
  return (
    <div className="wx-tj-card" onClick={onOpen}>
      <div className="wx-tj-card-top">
        <div className="wx-tj-employer-cell">
          <span className="wx-tj-avatar" aria-hidden="true">
            {monogram(item.displayName || item.legalName)}
          </span>
          <div>
            <strong>{item.legalName || "-"}</strong>
            <span>{item.employerCode}</span>
          </div>
        </div>
        <span className={`wx-badge wx-badge--${statusBadgeVariant(item.employerStatus)}`}>{item.employerStatus}</span>
      </div>
      <div className="wx-tj-card-grid">
        <div>
          <label>Agent</label>
          <strong>
            {item.agent?.agentCode ?? "-"} · {item.agent?.displayName ?? "-"}
          </strong>
        </div>
        <div>
          <label>สายงาน</label>
          <strong>
            {item.line?.superAgentName ?? "-"} · {item.line?.plcName ?? "-"}
          </strong>
        </div>
        <div>
          <label>งานเปิดรับ</label>
          <strong>{item.openJobCount}</strong>
        </div>
        <div>
          <label>ต้องการแรงงาน</label>
          <strong>{item.totalDemandCount} อัตรา</strong>
        </div>
        <div>
          <label>ผู้ติดต่อ</label>
          <strong className="wx-tj-reserved-text">{CONTACT_RESERVED_TEXT}</strong>
        </div>
      </div>
      <button
        type="button"
        className="wx-tj-row-action wx-tj-row-action--full"
        onClick={(event) => {
          event.stopPropagation();
          onOpenJobs();
        }}
      >
        งาน
      </button>
    </div>
  );
}

/**
 * The employer record behind a row - one screen, no tabs.
 *
 * For anyone who may write to it, that screen is the create-employer form
 * opened on this employer, every field carrying what is stored, with Save:
 * clicking a row and being shown a wall of text you then have to find an edit
 * route for was the long way round to the same screen. Read-only roles (PLC /
 * Super Agent) still get the read-only pane, because an editable form they may
 * not submit would be a lie. The employer's positions are not here at all -
 * the row's own งาน button opens them.
 */
function EmployerDetailView({
  titleId,
  state,
  primaryRole,
  editForm,
  onEditChange,
  onEditSubmit,
  onCloseDrawer,
  onMediaChanged,
  editSubmitState,
  editErrorText,
  employerOptions,
}) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียด..." />
    );
  }

  if (state.status === "error") {
    return (
      <div className="wx-tj-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดรายละเอียดได้
        </h2>
        <p className="wx-state-body">{describeDetailError(state.error)}</p>
      </div>
    );
  }

  const detail = state.data;
  const nextActions = detail.nextActions ?? {};
  // The record is editable only when the API says this actor may edit it and
  // the edit form is actually holding this employer's own values.
  const canEditEmployer =
    nextActions.canEditEmployer === true && !VIEW_ONLY_ROLES.includes(primaryRole) && editForm?.employerId === detail.employerId;

  return (
    <div className="wx-tj-drawer">
      {/* Just the identity. The kicker, the three standing badges and a second
          "แก้ไขข้อมูลนายจ้าง" heading under this one used four lines to say what
          the row already said - and pushed the fields the operator came for
          below the fold. The employer's status is a field in the form now, and
          the Invite-Card fact is stated once, on the page itself. */}
      <h2 id={titleId} className="wx-tj-drawer-title wx-tj-drawer-title--record">
        {detail.legalName || detail.displayName || "รายละเอียดนายจ้าง"}
      </h2>
      <p className="wx-tj-drawer-sub">
        {detail.employerCode} · สร้างเมื่อ {formatDateTimeTh(detail.createdAtUtc) ?? "-"}
      </p>

      {/* No tabs left, and nothing but the record itself. The middle
          "ผู้ติดต่อ/ไซต์งาน" tab is gone (the contact's own channels are part of
          the record below; worksites are not used in this product at all), and
          so is the "งาน" tab, because the
          row's own งาน button opens this employer's positions. */}
      {canEditEmployer ? (
        <EmployerFormView
          mode="edit"
          embedded
          form={editForm}
          onChange={onEditChange}
          onSubmit={onEditSubmit}
          onCancel={onCloseDrawer}
          submitState={editSubmitState}
          errorText={editErrorText}
          employerOptions={employerOptions}
          employer={detail}
          primaryRole={primaryRole}
          onMediaChanged={onMediaChanged}
        />
      ) : (
        <IdentityPane detail={detail} />
      )}
    </div>
  );
}

/**
 * P160 display helpers. All of them return "-" for a missing value rather than
 * inventing one: an employer whose registration date nobody recorded shows as
 * not recorded, which is information.
 */
function formatThaiDate(value) {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
}

function formatBaht(value) {
  if (value == null) {
    return "-";
  }
  return `${Number(value).toLocaleString("th-TH")} บาท`;
}

/** 00000 is the tax code for a head office; anything else is a branch number. */
function formatTaxBranch(value) {
  if (!value) {
    return "-";
  }
  return value === "00000" ? "สำนักงานใหญ่" : `สาขา ${value}`;
}

/**
 * Prefers the structured address parts and falls back to the legacy free-text
 * column, so employers created before P160 still show the address they have.
 */
function formatEmployerAddress(detail) {
  const parts = [
    detail.addressLine,
    detail.subDistrict && `ต.${detail.subDistrict}`,
    detail.district && `อ.${detail.district}`,
    detail.province,
    detail.postalCode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : detail.addressText || "-";
}

function IdentityPane({ detail }) {
  return (
    <>
      <section className="wx-tj-dsec wx-tj-dsec-row">
        <div className="wx-tj-dsec-col">
          <h3>ข้อมูลนายจ้าง</h3>
          <div className="wx-tj-identity-block">
            <span className="wx-tj-avatar wx-tj-avatar--lg" aria-hidden="true">
              {monogram(detail.displayName || detail.legalName)}
            </span>
            <div className="wx-tj-identity-block-text">
              <strong>{detail.legalName || "-"}</strong>
              <span>
                {detail.displayName ?? "-"} · {detail.employerCode}
              </span>
            </div>
          </div>
          {/* P160: ประเภทธุรกิจ used to be dropped here with a note saying the
              detail contract did not return it. It does now - along with the
              rest of the detailed record - so the row is real again. */}
          <div className="wx-tj-kv-row">
            <span>ประเภทธุรกิจ</span>
            <strong>{detail.businessType ?? "-"}</strong>
          </div>
          <div className="wx-tj-kv-row">
            <span>ประเภทนิติบุคคล</span>
            <strong>{detail.entityType ?? "-"}</strong>
          </div>
          <div className="wx-tj-kv-row">
            <span>เลขผู้เสียภาษี</span>
            <strong className="wx-tj-code">{detail.taxIdMasked ?? "-"}</strong>
          </div>
          <div className="wx-tj-kv-row">
            <span>สาขา</span>
            <strong>{formatTaxBranch(detail.taxBranchCode)}</strong>
          </div>
          <div className="wx-tj-kv-row">
            <span>วันที่จดทะเบียน</span>
            <strong>{formatThaiDate(detail.establishedOn)}</strong>
          </div>
          <div className="wx-tj-kv-row">
            <span>ทุนจดทะเบียน</span>
            <strong>{formatBaht(detail.registeredCapital)}</strong>
          </div>
          <div className="wx-tj-kv-row">
            <span>ขนาดกิจการ</span>
            <strong>{detail.employeeCountRange ?? "-"}</strong>
          </div>
          <div className="wx-tj-kv-row">
            <span>สถานะนายจ้าง</span>
            <strong>{detail.employerStatus}</strong>
          </div>
          <div className="wx-tj-kv-row wx-tj-kv-row--stack">
            <span>ที่อยู่</span>
            <strong>{formatEmployerAddress(detail)}</strong>
          </div>
          {detail.companyBackground && (
            <div className="wx-tj-kv-row wx-tj-kv-row--stack">
              <span>ความเป็นมาบริษัท</span>
              <strong className="wx-tj-longtext">{detail.companyBackground}</strong>
            </div>
          )}
        </div>
        <div className="wx-tj-dsec-col">
          <h3>Agent เจ้าของและสายงาน</h3>
          <div className="wx-tj-kv-row">
            <span>Agent เจ้าของ</span>
            <strong>
              {detail.agent?.agentCode ?? "-"} · {detail.agent?.displayName ?? "-"}
            </strong>
          </div>
          <div className="wx-tj-kv-row">
            <span>Super Agent</span>
            <strong>{detail.line?.superAgentName ?? "-"}</strong>
          </div>
          <div className="wx-tj-kv-row">
            <span>PLC / บมจ</span>
            <strong>{detail.line?.plcName ?? "-"}</strong>
          </div>
          <div className="wx-tj-kv-row">
            <span>สร้างเมื่อ</span>
            <strong>{formatDateTimeTh(detail.createdAtUtc) ?? "-"}</strong>
          </div>

          {/* The contact used to live behind a tab of its own. That
              tab is gone, and a read-only role has no form to read it from, so
              it belongs in the record they do get. */}
          <h3>ผู้ติดต่อ</h3>
          <div className="wx-tj-kv-row">
            <span>ผู้ติดต่อหลัก</span>
            <strong>
              {detail.primaryContactName ?? "-"}
              {detail.primaryContactPosition ? " · " + detail.primaryContactPosition : ""}
            </strong>
          </div>
          {(detail.contacts ?? []).map((contact, index) => (
            <div className="wx-tj-kv-row" key={`${contact.contactType}-${index}`}>
              <span>{contact.contactType ?? "ช่องทางติดต่อ"}</span>
              <strong>{contact.contactValue ?? "-"}</strong>
            </div>
          ))}
        </div>
      </section>
      <p className="wx-tj-note-muted wx-tj-note-muted--left">{TAX_ID_DETAIL_NOTE}</p>
    </>
  );
}
